import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Drawer, Form, Input, Select } from "antd";
import { ColumnProps } from "antd/lib/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/lib/form";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button } from "Components/General/Button";
import { observer } from "mobx-react";
import { emailInboxFilterStore } from "../../Stores/EmailInboxFilterStore";
import { modal } from "Components/General/Modal/AntdModal";
import { Table } from "Components/General/Table";

interface FiltersModalProps extends WithTranslation, ModalComponentProps {}

interface FiltersModalState {
  editingId: number; // to know if creating or editing a record
  visible: boolean;
  filterType: API_MailInboxFilter["filterType"];
}

const filterTypes: { value: API_MailInboxFilter["filterType"]; label: string }[] = [
  { value: "ATTACHMENT_CONTAINS", label: "Attachment contains" },
  { value: "ATTACHMENT_EXISTS", label: "Attachment exists" },
  { value: "ATTACHMENT_FULFILLS_CONVENTION", label: "Attachment fulfills filename rules" },
  { value: "SENDER", label: "Sender contains" },
  { value: "SUBJECT", label: "Subject contains" },
];

export const FiltersModal = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FiltersModalProps, FiltersModalState> {
      formRef: React.RefObject<FormInstance> = React.createRef();
      constructor(props: FiltersModalProps) {
        super(props);
        this.state = {
          editingId: null,
          visible: false,
          filterType: null, // track selected filterType to render Select or Input
        };
      }

      componentDidMount() {
        emailInboxFilterStore.fetch();
      }

      componentWillUnmount() {
        emailInboxFilterStore.reset();
      }

      showDrawer = (record: API_MailInboxFilter) => {
        this.setState({ visible: true, editingId: record.id, filterType: record.filterType }, () =>
          this.formRef.current.setFieldsValue(record)
        );
        this.props.setConfirmOnCancel(true);
      };

      createFilter = () => {
        this.setState({ visible: true });
        this.props.setConfirmOnCancel(true);
      };

      onClose = () => {
        this.setState({ visible: false, editingId: null, filterType: null }, () => this.formRef.current.resetFields());
        this.props.setConfirmOnCancel(false);
      };

      confirmDelete = (filterId: number) => {
        const { t } = this.props;
        const onOk = async () => {
          await emailInboxFilterStore.deleteItem(filterId);
          notificationSuccess(t("Success"), t("Filter deleted"));
        };
        modal.confirm({
          title: t("Delete filter?"),
          onOk,
          icon: <ExclamationCircleOutlined />,
          okText: t("Yes"),
          okType: "default",
          cancelButtonProps: { type: "primary" },
          cancelText: t("No"),
          zIndex: 1052,
        });
      };

      handleFinish = async (values) => {
        const { t } = this.props;
        const { editingId } = this.state;
        try {
          if (editingId) {
            await emailInboxFilterStore.editItem(editingId, values);
            notificationSuccess(t("Success"), t("Filter edited"));
          } else {
            await emailInboxFilterStore.createItem(values);
            notificationSuccess(t("Success"), t("Filter created"));
          }
          this.onClose();
        } catch (error) {
          throw error;
        }
      };

      handleFilterTypeChange = (_, values) => {
        this.setState({ filterType: values.filterType });
      };

      render() {
        const { t } = this.props;
        const columns: ColumnProps<API_MailInboxFilter>[] = [
          {
            dataIndex: "name",
            title: t("Name"),
          },
          {
            dataIndex: "used",
            title: t("Used in rules"),
            className: "text-center",
            render: (text, record) => (record.used ? t("Yes") : t("No")),
          },
          {
            dataIndex: "filterType",
            title: t("Filter definition"),
            render: (text, record) => {
              if (record.filterType === "ATTACHMENT_EXISTS") {
                return t("Attachment exists");
              }
              return `${t(filterTypes.find((item) => item.value === record.filterType)?.label)} "${record.value}"`;
            },
          },
          {
            dataIndex: "id",
            title: t("Actions"),
            width: 110,
            render: (_, rec) => (
              <>
                <Button size="small" type="link" onClick={() => this.showDrawer(rec)}>
                  <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} />
                </Button>
                {!rec.used && (
                  <Button size="small" type="link" onClick={() => this.confirmDelete(rec.id)}>
                    <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
                  </Button>
                )}
              </>
            ),
          },
        ];

        return (
          <div style={{ display: "flex", height: "100%", flexDirection: "column" }}>
            <div className="text-right">
              <Button type="primary" style={{ marginBottom: 10 }} faIcon={["far", "plus"]} onClick={this.createFilter}>
                {t("New filter")}
              </Button>
            </div>
            <Table
              name="mailFilters"
              rowKey="id"
              columns={columns}
              loading={emailInboxFilterStore.state === "LOADING"}
              dataSource={emailInboxFilterStore.list}
              pagination={{ hideOnSinglePage: true }}
            />
            <Drawer
              destroyOnHidden={true}
              title={t("Edit filter")}
              placement="right"
              closable={false}
              onClose={this.onClose}
              open={this.state.visible}
              getContainer={() => document.querySelector(".ant-modal-container")}
              style={{ position: "absolute" }}
              width={400}
              footer={
                <div className="text-right">
                  <Button
                    type="primary"
                    loading={emailInboxFilterStore.actionState === "LOADING"}
                    onClick={() => this.formRef.current.submit()}
                  >
                    {t("Save")}
                  </Button>
                </div>
              }
            >
              <Form
                ref={this.formRef}
                onFinish={this.handleFinish}
                layout="vertical"
                onValuesChange={this.handleFilterTypeChange}
              >
                <Form.Item
                  label={t("Name")}
                  name="name"
                  rules={[{ required: true, message: t("This field is required") }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t("Filter type")}
                  name="filterType"
                  rules={[{ required: true, message: t("This field is required") }]}
                >
                  <Select>
                    {filterTypes.map((item) => (
                      <Select.Option key={item.value} value={item.value}>
                        {t(item.label)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                {this.state.filterType === "ATTACHMENT_EXISTS" ? null : ( // hide input for true/false filter
                  <Form.Item
                    label={t("Filter value")}
                    name="value"
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    {this.state.filterType === "ATTACHMENT_FULFILLS_CONVENTION" ? (
                      <Select>
                        {emailInboxFilterStore.conventions.map((item) => (
                          <Select.Option key={item} value={item}>
                            {item}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <Input />
                    )}
                  </Form.Item>
                )}
              </Form>
            </Drawer>
          </div>
        );
      }
    }
  )
);
